/** Explicit source alternatives: saved authoring, scoped cleanup receipt, or a genuinely new creation intent. */
import type { ShopifyAppBridge } from '../../shopifyApiFetch'
import { shopifyComponentCopy } from '@testfeed/product-language'
import { MerchantFacingError } from '../../merchantErrors'
import { listStudyAuthoring, readStudyAuthoringStatus, readStudyAuthoringDeletionStatus } from './studyAuthoringClient'
export async function readDirectBuilderSource(
  shopify: ShopifyAppBridge,
  identity: { studyId?: string; creationIntentId?: string },
  signal: AbortSignal,
) {
  if (identity.studyId) {
    try {
      return {
        authoring: await readStudyAuthoringStatus(shopify, identity.studyId, signal),
        deletion: null,
        startingDraft: null,
      }
    } catch (error) {
      if (!(error instanceof MerchantFacingError) || error.status !== 404 || error.code !== 'STUDY_SCOPE_MISMATCH')
        throw error
      try {
        return {
          authoring: null,
          deletion: await readStudyAuthoringDeletionStatus(shopify, { studyId: identity.studyId }, signal),
          startingDraft: null,
        }
      } catch (deletionError) {
        if (deletionError instanceof MerchantFacingError && deletionError.status === 404) throw error
        throw deletionError
      }
    }
  }
  if (!identity.creationIntentId)
    throw new MerchantFacingError(shopifyComponentCopy.studyBuilderPage.identityMissing, {
      code: 'STUDY_AUTHORING_INVALID',
    })
  const page = await listStudyAuthoring(
    shopify,
    { creationIntentId: identity.creationIntentId, includeState: true, limit: 1 },
    signal,
  )
  const authoring = page.studies[0]
  // A brand-new creation intent has no saved study: the Worker sent the starting draft (and its seed) on `startingDraft`;
  // the app opens the form on it and never builds its own defaults.
  if (!authoring)
    return { authoring: null, deletion: page.deletions?.[0] ?? null, startingDraft: page.startingDraft ?? null }
  const state = page.states?.[authoring.studyId]
  if (!state || state.revision !== authoring.revision)
    throw new MerchantFacingError(shopifyComponentCopy.studyBuilderPage.restoreFailed, {
      code: 'STUDY_AUTHORING_INVALID',
    })
  return { authoring: { authoring, state }, deletion: null, startingDraft: null }
}
